import { useEffect } from 'react';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Banner, spaces, useBreakpoint } from '@electricjs/arc';

import { StorefrontSteps } from '@/types/storefront';

import { EmployeeSection } from '../../components/Sections/EmployeeSection';
import {
  StoreSection,
  StoreSectionProps,
} from '../../components/Sections/StoreSection';
import { useStorefront } from '../../StorefrontProvider';
import { SchemaOption } from '../StorefrontCheckoutFormProvider';
import CountryMismatchBanner from '../International/CountryMismatchBanner';
import CartButton from '../CartButton';
import {
  getShoppingCountryOptions,
  ShoppingCountrySelector,
} from '../ShoppingCountrySelector';
import OtherCountryFlow from '../International/OtherCountryFlow';

const FullWidthForm = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: ${spaces.space_l};
`;

export type ShoppingStepProps = StoreSectionProps & {
  isTiedToEmployee: boolean;
};

/**
 * Shopping step: Employee + Stores (Asset, Catalog) or OtherCountryFlow
 */
export const ShoppingStep = ({
  isTiedToEmployee,
  laptopRecommendationUrl,
}: ShoppingStepProps) => {
  const { t } = useTranslation('storefront');

  const { md } = useBreakpoint();

  const { storefrontInternational } = useFlags();

  const {
    storefrontCheckoutForm,
    setStorefrontStep,
    itemsInCart,
    setSchemaOption,
    otherCountryFlowStep,
    catalogCart,
    setOrderCurrency,
  } = useStorefront();

  useEffect(() => {
    if (isTiedToEmployee) {
      setSchemaOption(SchemaOption.EMPLOYEE);
    }
  }, [isTiedToEmployee, setSchemaOption]);

  const handleGoToCart = () => {
    // The form submit process was already handled by the form's lib (form validation, setting focus on the error field, etc)
    // After form is valid, just need to go to the Cart step (this step will pull data directly from the form's state)
    setStorefrontStep(StorefrontSteps.CART);
  };

  const employeeAddressCountry = storefrontCheckoutForm?.watch(
    'employee.address.country'
  );

  const selectedShoppingCountry = storefrontCheckoutForm?.watch(
    'shoppingCountry.value'
  );

  /** Country mismatch
   *  - When the employee's country is in the shopping country list: Mismatch happens when the selected shopping country is different from the employee's country
   *  - When the employee's country is NOT in the shopping country list (would be the "other" option): Mismatch happens when the selected shopping country is different from "other"
   */
  const countryMismatch =
    storefrontInternational &&
    employeeAddressCountry &&
    (getShoppingCountryOptions().some(
      option => option.value === employeeAddressCountry
    )
      ? employeeAddressCountry !== selectedShoppingCountry
      : selectedShoppingCountry !== 'other');

  // keep track of order currency
  useEffect(() => {
    // try to get primarily based on products currency
    const productCurrency =
      catalogCart.length > 0 ? catalogCart[0].item.currencyCode : undefined;

    if (productCurrency) {
      setOrderCurrency(productCurrency);
    } else {
      // secondarily, get based on shopping country
      if (selectedShoppingCountry === 'CA') {
        setOrderCurrency('CAD');
      } else {
        setOrderCurrency('USD'); // default to USD
      }
    }
  }, [catalogCart, selectedShoppingCountry, setOrderCurrency]);

  return (
    <FullWidthForm
      onSubmit={storefrontCheckoutForm?.handleSubmit(handleGoToCart)}>
      {countryMismatch && (
        <Box width="100%">
          <CountryMismatchBanner />
        </Box>
      )}

      {/* Row: Employee section + Shopping Country selector + Cart button */}
      {(selectedShoppingCountry !== 'other' || otherCountryFlowStep === 1) &&
        (md ? (
          <Flex
            width="100%"
            columnGap={spaces.space_xs_rem}
            justifyContent="space-between">
            {/* Row left-side: Employee section */}
            {isTiedToEmployee && <EmployeeSection width="45rem" />}

            {/* Row right-side: Shopping Country selector + Cart button */}
            <Flex
              mt="22px" // need to use margin-top instead of "align-items:center" to align vertically with Search employee component (specially when the input error message appears)
              columnGap={spaces.space_m_rem}>
              <ShoppingCountrySelector />
              {selectedShoppingCountry !== 'other' && (
                <CartButton itemsCount={itemsInCart} />
              )}
            </Flex>
          </Flex>
        ) : (
          // Mobile: Employee section stacked above Shopping Country selector, Cart button beside both
          <Flex
            width="100%"
            columnGap={spaces.space_m_rem}
            alignItems="flex-start">
            <Flex vertical flexGrow="1">
              {isTiedToEmployee && <EmployeeSection width="100%" />}
              <ShoppingCountrySelector
                width="100%"
                // the employee selector field has a built-in 16px bottom margin (from Arc's form fields); trim it down to an 8px gap
                // (only needed when the employee section renders above it)
                mt={isTiedToEmployee ? '-8px' : undefined}
              />
            </Flex>
            {selectedShoppingCountry !== 'other' && (
              <Box mt="2rem">
                <CartButton itemsCount={itemsInCart} />
              </Box>
            )}
          </Flex>
        ))}

      {/* Banner for employees with empty address on file
          - International FF must be enabled
          - Employee must be selected
          - Employee can't have an address on file (using street1 as reference)
      */}
      {storefrontInternational &&
        storefrontCheckoutForm?.getValues('employee.value') &&
        !storefrontCheckoutForm?.getValues(
          'employee.address.streetAddress1'
        ) && (
          <Box
            width="100%"
            mt="-16px" // negative margin to avoid the extra 16px of margin added by the employee selector field (it's a built-in margin that comes from Arc's form fields)
          >
            <Banner
              id="empty-address-banner"
              isFlat
              intent="warning"
              isFullWidth
              title={t('steps.shopping.emptyAddressBanner.title')}
              message={t('steps.shopping.emptyAddressBanner.message')}
            />
          </Box>
        )}

      {/* Store Section or OtherCountryFlow */}
      {selectedShoppingCountry === 'other' ? (
        <OtherCountryFlow />
      ) : (
        <StoreSection laptopRecommendationUrl={laptopRecommendationUrl} />
      )}
    </FullWidthForm>
  );
};
